import React, { Fragment, useState, useEffect } from 'react';
import { Edit, Trash, ChevronDown, ChevronUp, X, AlertTriangle, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  useListPermissions,
  useCreatePermission,
  useUpdatePermission,
  useDeletePermission,
} from '@/hooks/useIAM';
import { Permission, CreatePermissionData, UpdatePermissionData } from '@/types/iam';

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  permissionName,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  permissionName: string;
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen) return null;

  const handleConfirmDelete = async () => {
    setIsDeleting(true);

    const toastId = toast.loading(`Deleting permission "${permissionName}"...`);

    try {
      await onConfirm();

      toast.success(`Permission "${permissionName}" deleted successfully`, { id: toastId });

      onClose();
    } catch (error) {
      console.error('Error deleting permission:', error);

      toast.error(`Error deleting permission "${permissionName}". Please try again.`, {
        id: toastId,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 bg-black/50"
    >
      <div className="rounded-lg shadow-lg max-w-md w-full bg-background">
        <div className="p-5 border-b border-border flex justify-between items-center">
          <div className="flex items-center text-secondary">
            <AlertTriangle className="mr-2" size={20} />
            <h3 className="text-lg font-medium">Confirm Delete</h3>
          </div>
          <button
            onClick={onClose}
            className="hover:opacity-70 transition-opacity text-muted-foreground"
            disabled={isDeleting}
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5">
          <p className="mb-4 text-foreground">
            Are you sure you want to delete the permission{' '}
            <span className="font-semibold">{permissionName}</span>? This action cannot be undone.
          </p>

          <div className="flex justify-end space-x-3 mt-5">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-muted-foreground rounded-md transition-colors bg-background text-foreground hover:bg-muted-foreground/10"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 rounded-md transition-colors flex items-center bg-destructive text-destructive-foreground hover:opacity-90"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PermissionForm = ({
  permission,
  isOpen,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  permission?: Permission;
  isOpen: boolean;
  onSubmit: (data: CreatePermissionData | UpdatePermissionData) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}) => {
  const [formData, setFormData] = useState({
    id: permission?.id || '',
    name: permission?.name || '',
    description: permission?.description || '',
    resource: permission?.resource || '',
    priority: permission?.priority || 0,
  });

  useEffect(() => {
    setFormData({
      id: permission?.id || '',
      name: permission?.name || '',
      description: permission?.description || '',
      resource: permission?.resource || '',
      priority: permission?.priority || 0,
    });
  }, [permission]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      ...(permission && { id: permission.id }),
    };

    await onSubmit(submitData);
  };

  return (
    isOpen && (
      <div
        className="fixed inset-0 flex items-center justify-center z-50 bg-black/50"
      >
        <div className="rounded-lg shadow-lg max-w-md w-full bg-background">
          <div className="p-5 border-b border-border flex justify-between items-center">
            <h3 className="text-lg font-medium text-foreground">
              {permission ? 'Edit Permission' : 'Create Permission'}
            </h3>
            <button
              onClick={onCancel}
              className="hover:opacity-70 transition-opacity text-muted-foreground"
              disabled={isSubmitting}
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-5">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-foreground">
                Id *
              </label>
              <input
                type="text"
                required
                disabled={!!permission}
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                className="w-full px-3 py-2 border border-muted-foreground rounded-md focus:outline-none focus:ring-2 bg-background text-foreground"
                placeholder="Example: area:resource.action"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-foreground">
                Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-muted-foreground rounded-md focus:outline-none focus:ring-2 bg-background text-foreground"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-foreground">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-muted-foreground rounded-md focus:outline-none focus:ring-2 bg-background text-foreground"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-foreground">
                Priority
              </label>
              <input
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-muted-foreground rounded-md focus:outline-none focus:ring-2 bg-background text-foreground"
              />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2 text-foreground">
                Resource
              </label>
              <input
                type="text"
                value={formData.resource}
                onChange={(e) => setFormData({ ...formData, resource: e.target.value })}
                className="w-full px-3 py-2 border border-muted-foreground rounded-md focus:outline-none focus:ring-2 bg-background text-foreground"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-muted-foreground rounded-md transition-colors bg-background text-foreground hover:bg-muted-foreground/10"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-md transition-colors flex items-center bg-primary text-primary-foreground hover:opacity-90"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    {permission ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  permission ? 'Update' : 'Create'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );
};

const PermissionsTable = () => {
  const { permissions, fetchPermissions, loading } = useListPermissions();
  const { createPermission, loading: createLoading } = useCreatePermission();
  const { updatePermission, loading: updateLoading } = useUpdatePermission();
  const { deletePermission } = useDeletePermission();

  const [sortField, setSortField] = useState<keyof Permission>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedPermission, setSelectedPermission] = useState<Permission | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Initialize data
  React.useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const showDeleteConfirmation = (id: string) => {
    setDeleteId(id);
    setIsModalOpen(true);
  };

  const handleDeletePermission = async () => {
    if (deleteId) {
      await deletePermission(deleteId);
      await fetchPermissions();
      setIsModalOpen(false);
      setDeleteId(null);
    }
  };

  const handleSort = (field: keyof Permission) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedPermissions = [...permissions].sort((a, b) => {
    const aValue = (a[sortField] as string)?.toLowerCase() || '';
    const bValue = (b[sortField] as string)?.toLowerCase() || '';

    if (sortDirection === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  const renderSortIndicator = (field: keyof Permission) => {
    if (sortField !== field) return null;

    return sortDirection === 'asc' ? (
      <ChevronUp size={16} className="ml-1" />
    ) : (
      <ChevronDown size={16} className="ml-1" />
    );
  };

  const handleEditPermission = (permission: Permission) => {
    console.log('editing permission', permission);
    setSelectedPermission(permission);
    setIsFormOpen(true);
  };

  const handleCreatePermission = () => {
    setSelectedPermission(null);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (data: CreatePermissionData | UpdatePermissionData) => {
    try {
      if (selectedPermission) {
        await updatePermission(data as UpdatePermissionData);
      } else {
        await createPermission(data as CreatePermissionData);
      }
      await fetchPermissions();
      setIsFormOpen(false);
      setSelectedPermission(null);
    } catch (error) {
      // Error handling is done in the hooks
    }
  };

  const permissionToDelete = deleteId
    ? permissions.find((permission) => permission.id === deleteId)?.name || 'this permission'
    : '';

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header with title and button to add */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-foreground">Permissions</h2>

        <button
          onClick={handleCreatePermission}
          className="px-4 py-2 rounded-md transition-colors flex items-center bg-primary text-primary-foreground hover:opacity-90"
        >
          <Plus size={16} className="mr-2" />
          Add Permission
        </button>
      </div>

      {/* Table of permissions */}
      <div className="overflow-x-auto rounded-lg shadow bg-background">
        <table className="min-w-full divide-y bg-background">
          <thead className="">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-primary"
              >
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center focus:outline-none text-primary"
                >
                  Name {renderSortIndicator('name')}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden md:table-cell text-primary"
              >
                <button
                  onClick={() => handleSort('description')}
                  className="flex items-center focus:outline-none text-primary"
                >
                  Description {renderSortIndicator('description')}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden lg:table-cell text-primary"
              >
                <button
                  onClick={() => handleSort('resource')}
                  className="flex items-center focus:outline-none text-primary"
                >
                  Resource {renderSortIndicator('resource')}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-primary"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-background divide-y divide-border w-full">
            {sortedPermissions.length === 0 ? (
              <tr className="w-full">
                <td colSpan={4} className="px-6 py-8 text-center w-full">
                  No permissions have been added yet. Use the "Add Permission" button to create one.
                </td>
              </tr>
            ) : (
              sortedPermissions.map((permission) => (
                <Fragment key={permission.id}>
                  <tr className="hover:bg-muted/50 cursor-pointer transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="ml-4">
                          <div className="text-sm font-medium text-primary">{permission.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      <div
                        className="text-sm truncate max-w-xs text-muted-foreground"
                      >
                        {permission.description || 'No description'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                      <div
                        className="text-sm truncate max-w-xs text-muted-foreground"
                      >
                        {permission.resource || 'No resource'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditPermission(permission)}
                        className="ml-4 transition-colors text-primary hover:opacity-70"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => showDeleteConfirmation(permission.id)}
                        className="ml-4 transition-colors text-destructive hover:opacity-70"
                      >
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      <PermissionForm
        permission={selectedPermission || undefined}
        onSubmit={handleFormSubmit}
        isOpen={isFormOpen}
        onCancel={() => {
          setIsFormOpen(false);
          setSelectedPermission(null);
        }}
        isSubmitting={createLoading || updateLoading}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleDeletePermission}
        permissionName={permissionToDelete}
      />
    </div>
  );
};

export default PermissionsTable;
